import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { api, type ApiResponse } from '@/lib/api';

/** Item cru retornado por `GET /api/units/dropdown` (backend Task 4). */
interface UnitDropdownItem {
  id: string;
  name: string;
  code?: string;
  /** UF do endereço, já em maiúscula. Ausente = unidade sem endereço. */
  state?: string;
}

export interface UnitDropdownOption {
  value: string;
  label: string;
  /** UF do endereço (`SC`) — para quem agrupa ou filtra por estado. */
  state?: string;
}

/**
 * Hook para listar unidades ATIVAS em formato de dropdown.
 * Usa `GET /api/units/dropdown` — rota cross-module sem `permissionMiddleware`
 * (só app + auth). Label combina nome + código entre parênteses quando houver.
 *
 * `enabled` (default `true`) — condicionar a `isOpen` em modais/drawers evita
 * disparar a query com o componente fechado (CLAUDE.md SEMPRE #10).
 */
export const useUnitsDropdown = (
  enabled: boolean = true
): UseQueryResult<UnitDropdownOption[], Error> => {
  return useQuery({
    queryKey: ['units-dropdown'],
    queryFn: async () => {
      const response = await api.get<ApiResponse<UnitDropdownItem[]>>('/units/dropdown');
      return (response.data.data ?? []).map((item) => ({
        value: item.id,
        label: item.code ? `${item.name} (${item.code})` : item.name,
        ...(item.state ? { state: item.state } : {}),
      }));
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });
};
